import { AlertTriangle, X } from "lucide-react";
import { useEffect } from "react";

export interface InUseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional explicit title — falls back to "Cannot delete". */
  title?: string;
  /** Main message body. Plain string; line-break allowed via `\n`. */
  message: string;
  /**
   * Optional list of bullet hints shown below the message
   * (e.g. ["Reassign the 12 users to another role", "Then retry the delete"]).
   */
  hints?: string[];
  /** Optional CTA — button label. If omitted, renders just an "OK" button. */
  ctaLabel?: string;
  onCtaClick?: () => void;
}

/**
 * "Cannot delete — X is in use" modal.
 *
 * Used when an entity (Role, Group, …) cannot be deleted because it's still
 * referenced by other records. Pure informational dialog — no destructive action.
 */
function InUseDialog({
  isOpen,
  onClose,
  title = "Cannot delete",
  message,
  hints,
  ctaLabel,
  onCtaClick,
}: InUseDialogProps) {
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inuse-dialog-title"
      onMouseDown={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden animate-[fadeIn_140ms_ease-out]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header band */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-b from-amber-50 to-white border-b border-amber-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-white/60 transition-colors"
            aria-label="Close"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center ring-4 ring-amber-50 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3
                id="inuse-dialog-title"
                className="text-base font-semibold text-gray-900"
              >
                {title}
              </h3>
              <p className="mt-1 text-sm text-gray-600 whitespace-pre-line break-words">
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Hints */}
        {hints && hints.length > 0 && (
          <div className="px-6 py-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              What you can do
            </p>
            <ul className="space-y-1.5">
              {hints.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
          {ctaLabel && onCtaClick && (
            <button
              type="button"
              onClick={() => {
                onCtaClick();
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              {ctaLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default InUseDialog;
